import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { subscriptionService } from '@/services/subscriptionService';
import { useTenantStore } from '@/hooks/useTenantStore';
import { useAuthStore } from '@/hooks/useAuthStore';

interface SubscriptionAgreementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  shareholders: Array<{ id: string; name: string }>;
}

export function SubscriptionAgreementDialog({
  open,
  onOpenChange,
  onSuccess,
  shareholders,
}: SubscriptionAgreementDialogProps) {
  const { tenantId } = useTenantStore();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    shareholder_id: '',
    shares_offered: '',
    price_per_share: '',
    share_class: 'ORDINARY',
    payment_terms: '',
    payment_due_date: '',
    expires_at: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tenantId || !user) {
      toast.error('Missing company or user information');
      return;
    }

    if (!formData.shareholder_id || !formData.shares_offered || !formData.price_per_share) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await subscriptionService.createSubscription({
        company_id: tenantId,
        shareholder_id: formData.shareholder_id || null,
        shares_offered: parseInt(formData.shares_offered),
        price_per_share: parseFloat(formData.price_per_share),
        share_class: formData.share_class,
        payment_terms: formData.payment_terms || null,
        payment_due_date: formData.payment_due_date || null,
        expires_at: formData.expires_at || null,
        notes: formData.notes || null,
        status: 'DRAFT',
        created_by: user.id,
        agreement_number: null,
        payment_received_date: null,
        issued_date: null,
        ledger_event_id: null,
      });

      toast.success('Subscription agreement created successfully');
      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error('Failed to create subscription agreement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      shareholder_id: '',
      shares_offered: '',
      price_per_share: '',
      share_class: 'ORDINARY',
      payment_terms: '',
      payment_due_date: '',
      expires_at: '',
      notes: '',
    });
  };

  const totalAmount =
    formData.shares_offered && formData.price_per_share
      ? (parseFloat(formData.shares_offered) * parseFloat(formData.price_per_share)).toFixed(2)
      : '0.00';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Subscription Agreement</DialogTitle>
          <DialogDescription>
            Issue new shares to investors via subscription agreement
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="shareholder">Subscriber *</Label>
            <Select
              value={formData.shareholder_id}
              onValueChange={(value) => setFormData({ ...formData, shareholder_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select subscriber" />
              </SelectTrigger>
              <SelectContent>
                {shareholders.map((sh) => (
                  <SelectItem key={sh.id} value={sh.id}>
                    {sh.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shares">Shares Offered *</Label>
              <Input
                id="shares"
                type="number"
                min="1"
                value={formData.shares_offered}
                onChange={(e) => setFormData({ ...formData, shares_offered: e.target.value })}
                placeholder="1000"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Price per Share *</Label>
              <Input
                id="price"
                type="number"
                step="0.0001"
                min="0.0001"
                value={formData.price_per_share}
                onChange={(e) => setFormData({ ...formData, price_per_share: e.target.value })}
                placeholder="10.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="shareClass">Share Class</Label>
            <Input
              id="shareClass"
              value={formData.share_class}
              onChange={(e) => setFormData({ ...formData, share_class: e.target.value })}
              placeholder="ORDINARY"
            />
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Total Amount:</span>
              <span className="text-lg font-bold">${totalAmount}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_terms">Payment Terms</Label>
            <Textarea
              id="payment_terms"
              value={formData.payment_terms}
              onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
              placeholder="e.g., Payment due within 30 days of acceptance"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="payment_due">Payment Due Date</Label>
              <Input
                id="payment_due"
                type="date"
                value={formData.payment_due_date}
                onChange={(e) => setFormData({ ...formData, payment_due_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires">Agreement Expires</Label>
              <Input
                id="expires"
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes or conditions"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Agreement'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
